/**
 * PaneFilterSort — compact sort + filter control rendered inside the
 * Daily Dashboard V2 Backlog / Unplanned pane headers (replaces the
 * legacy "+ Add" button — those panes only ACCEPT tasks via drag from
 * the planner).
 *
 * UI: two compact pill buttons that match the .dp-badge size + style and
 * open a Popover with the actual controls. This prevents the wide native
 * <select> chrome from overlapping the count / effort pills (Feb 2026
 * iteration after first implementation overlapped on narrow panes).
 *
 *   value = { sort, dir, filter, status }
 */
import React from 'react';
import { Popover, PopoverTrigger, PopoverContent } from '../ui/popover';
import { ArrowDownUp, Filter, ArrowUp, ArrowDown, Check } from 'lucide-react';

const SORT_OPTIONS = [
  { value: 'priority', label: 'Priority' },
  { value: 'effort',   label: 'Effort'   },
  { value: 'date',     label: 'Date'     },
  { value: 'title',    label: 'Title'    },
];

const STATUS_OPTIONS = [
  { value: '',           label: 'Any status' },
  { value: 'To Do',      label: 'To Do' },
  { value: 'In Progress',label: 'In Progress' },
  { value: 'Blocked',    label: 'Blocked' },
  { value: 'Done',       label: 'Done' },
];

function PillButton({ icon: Icon, label, count, active, testId, ...rest }) {
  return (
    <button
      type="button"
      data-testid={testId}
      data-active={active ? 'true' : undefined}
      className="dp-pane-fs-pill"
      {...rest}
    >
      <Icon style={{ width: 11, height: 11 }} />
      <span>{label}</span>
      {count != null && count > 0 && (
        <span className="dp-pane-fs-count">{count}</span>
      )}
    </button>
  );
}

export default function PaneFilterSort({ value, onChange, workAreas = [], testId }) {
  const v = value || { sort: 'priority', dir: 'desc', filter: '', status: '' };
  const set = (patch) => onChange?.({ ...v, ...patch });

  const wa = (workAreas || []).find(w => (w.work_area_id || w.id) === v.filter);
  const activeFilters = (v.filter ? 1 : 0) + (v.status ? 1 : 0);
  const sortLabel = SORT_OPTIONS.find(o => o.value === v.sort)?.label || 'Priority';

  return (
    <div className="dp-pane-fs" data-testid={testId} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      {/* SORT */}
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="dp-pane-fs-pill"
            data-testid={testId ? `${testId}-sort-btn` : undefined}
            title={`Sort by ${sortLabel} ${v.dir === 'asc' ? 'asc' : 'desc'}`}
          >
            <ArrowDownUp style={{ width: 11, height: 11 }} />
            <span>{sortLabel}</span>
            {v.dir === 'asc' ? <ArrowUp style={{ width: 10, height: 10 }} /> : <ArrowDown style={{ width: 10, height: 10 }} />}
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-44 p-1">
          <div className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 orka-text-tertiary">Sort by</div>
          {SORT_OPTIONS.map(o => (
            <button
              key={o.value}
              type="button"
              onClick={() => set({ sort: o.value })}
              className="w-full text-left px-2 py-1.5 rounded text-xs flex items-center gap-2 hover:orka-surface-subtle"
              data-testid={testId ? `${testId}-sort-${o.value}` : undefined}
            >
              {v.sort === o.value ? <Check className="w-3 h-3 text-emerald-500" /> : <span className="w-3" />}
              {o.label}
            </button>
          ))}
          <div className="border-t orka-border-subtle my-1" />
          <button
            type="button"
            onClick={() => set({ dir: v.dir === 'asc' ? 'desc' : 'asc' })}
            className="w-full text-left px-2 py-1.5 rounded text-xs flex items-center gap-2 hover:orka-surface-subtle"
            data-testid={testId ? `${testId}-dir` : undefined}
          >
            {v.dir === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
            {v.dir === 'asc' ? 'Ascending' : 'Descending'}
          </button>
        </PopoverContent>
      </Popover>

      {/* FILTER */}
      <Popover>
        <PopoverTrigger asChild>
          <PillButton
            icon={Filter}
            label="Filter"
            count={activeFilters}
            active={activeFilters > 0}
            testId={testId ? `${testId}-filter-btn` : undefined}
            title={activeFilters > 0 ? `${activeFilters} active filter${activeFilters>1?'s':''}` : 'Filter'}
          />
        </PopoverTrigger>
        <PopoverContent align="end" className="w-56 p-1">
          <div className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 orka-text-tertiary">Work area</div>
          <button
            type="button"
            onClick={() => set({ filter: '' })}
            className="w-full text-left px-2 py-1.5 rounded text-xs flex items-center gap-2 hover:orka-surface-subtle"
          >
            {!v.filter ? <Check className="w-3 h-3 text-emerald-500" /> : <span className="w-3" />}
            All work areas
          </button>
          {(workAreas || []).map(w => (
            <button
              key={w.work_area_id || w.id}
              type="button"
              onClick={() => set({ filter: w.work_area_id || w.id })}
              className="w-full text-left px-2 py-1.5 rounded text-xs flex items-center gap-2 hover:orka-surface-subtle"
              data-testid={testId ? `${testId}-filter-wa-${w.work_area_id || w.id}` : undefined}
            >
              {v.filter === (w.work_area_id || w.id) ? <Check className="w-3 h-3 text-emerald-500" /> : <span className="w-3" />}
              <span
                className="w-2.5 h-2.5 rounded-full flex-none border"
                style={{ background: w.color || '#94a3b8', borderColor: 'rgba(15,23,42,.18)' }}
              />
              <span className="truncate">{w.label || w.name || w.work_area_id || w.id}</span>
            </button>
          ))}
          <div className="border-t orka-border-subtle my-1" />
          <div className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 orka-text-tertiary">Status</div>
          {STATUS_OPTIONS.map(o => (
            <button
              key={o.value || 'any'}
              type="button"
              onClick={() => set({ status: o.value })}
              className="w-full text-left px-2 py-1.5 rounded text-xs flex items-center gap-2 hover:orka-surface-subtle"
            >
              {v.status === o.value ? <Check className="w-3 h-3 text-emerald-500" /> : <span className="w-3" />}
              {o.label}
            </button>
          ))}
          {activeFilters > 0 && (
            <>
              <div className="border-t orka-border-subtle my-1" />
              <button
                type="button"
                onClick={() => set({ filter: '', status: '' })}
                className="w-full text-left px-2 py-1.5 rounded text-xs hover:orka-surface-subtle text-amber-600 font-bold"
                data-testid={testId ? `${testId}-clear` : undefined}
              >
                Clear filters
              </button>
            </>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
}

/** Apply the sort+filter `value` to a flat task array. */
export function applyPaneFilterSort(tasks, value) {
  if (!Array.isArray(tasks)) return [];
  const v = value || { sort: 'priority', dir: 'desc', filter: '', status: '' };
  let out = tasks;
  if (v.filter) {
    out = out.filter(t => t && t.work_area_id === v.filter);
  }
  if (v.status) {
    out = out.filter(t => t && t.task_status === v.status);
  }
  const dir = v.dir === 'asc' ? 1 : -1;
  const cmp = (a, b) => {
    const k = v.sort;
    if (k === 'priority') return dir * ((a.task_priority ?? 99) - (b.task_priority ?? 99));
    if (k === 'effort')   return dir * ((a.task_estimated_effort ?? 0) - (b.task_estimated_effort ?? 0));
    if (k === 'date')     return dir * String(a.task_delivery_date || a.task_local_date || '').localeCompare(String(b.task_delivery_date || b.task_local_date || ''));
    if (k === 'title')    return dir * String(a.task_title || '').localeCompare(String(b.task_title || ''));
    return 0;
  };
  return [...out].sort(cmp);
}
